/**
 * Format — pure frontend formatting + small utility helpers (no DOM, no app state).
 *
 * Extracted verbatim from script.js as the first, lowest-risk step of the god-object refactor.
 * Bundled into dist/index-*.js and exposed as window.WoxomQuote.Format; script.js aliases these
 * back to its local names so every existing call site keeps working unchanged.
 */

/** Escape a value for safe interpolation into an HTML string. */
export function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** "$1,234" — locale-grouped dollar amount (whole-number style). */
export function formatCurrency(value) {
  return '$' + value.toLocaleString();
}

/** A Date → "YYYY-MM-DD" for <input type="date"> values. */
export function formatDateForInput(date) {
  var year = date.getFullYear();
  var month = String(date.getMonth() + 1).padStart(2, '0');
  var day = String(date.getDate()).padStart(2, '0');
  return year + '-' + month + '-' + day;
}

/** Trailing-edge debounce: calls fn at most once per `delay` ms of quiet. */
export function debounce(fn, delay) {
  var timer;
  return function() {
    var context = this, args = arguments;
    clearTimeout(timer);
    timer = setTimeout(function() { fn.apply(context, args); }, delay);
  };
}

// The verbose "per person, per calendar year" / ", per calendar year" usage qualifier (kept as a
// constant so the same phrase is stripped identically inside parens and as a bare trailing tail).
const _BENEFIT_QUALIFIER = /,?\s*per person,?\s*per calendar year|,?\s*per calendar year/gi;

/**
 * Strip boilerplate from a carrier benefit-coverage string (UHOne details panel). Curated
 * rule-by-rule with the product owner — keeps real benefit caps, removes only repetitive noise:
 *   A. brochure pointers — parenthesized "(see … brochure …)" AND bare "; see … brochure …" tails
 *      (the panel already shows a brochure link)
 *   B. inside a parenthetical, drop the "per person, per calendar year" qualifier but KEEP the cap:
 *      "(maximum 1 exam per person, per calendar year)" → "(maximum 1 exam)";
 *      "(unlimited days per person, per calendar year)" → "(unlimited days)";
 *      "(combined maximum, per calendar year)" → "(combined maximum)"
 *   C. drop the lone "(maximum once per lifetime)" cap
 *   D. drop a bare qualifier-only paren "(per person, per calendar year)" (becomes empty → removed)
 *   E. drop a bare TRAILING "… per person, per calendar year" qualifier (end / before . or ;) —
 *      prose occurrences mid-sentence are preserved
 *   F. drop "Increased benefit/maximum starting in Nth calendar year" forward-references
 *   G. collapse \r\n line-breaks + double spaces (HTML-equivalent)
 *   + remove the filler word "policy" (capitalizing the new leading word), the
 *     "(additional $N lifetime deductible …)" rider note, the "in most states" qualifier, and the
 *     generic "Benefits are paid in addition to other insurance coverage …" disclaimer.
 * KEEPS structural parentheticals: (Tier N), (combined …), (includes observation unit …),
 * (If not admitted, you pay …), (one-time, non-refundable). Unknown parentheticals are kept.
 */
export function stripBenefitBoilerplate(value) {
  if (value == null) return '';
  var s = String(value).replace(/\s+/g, ' ').trim();  // G: collapse \r\n + runs early

  // A (parenthesized) + B + C + D + the (additional …) rider — one pass over each parenthetical
  // Dropped parens return ' ' (not '') so a paren flush against the preceding word can't glue two
  // words together; the final \s+ collapse + trim tidy the extra space.
  s = s.replace(/\s*\(([^)]*)\)/g, function(m, inner) {
    var t = inner.trim();
    if (/brochure/i.test(t)) return ' ';                                           // A
    if (/^maximum once per lifetime$/i.test(t)) return ' ';                        // C
    if (/^additional \$\d[\d,]* lifetime deductible/i.test(t)) return ' ';         // (additional …) rider
    var cleaned = t.replace(_BENEFIT_QUALIFIER, '').replace(/\s+/g, ' ').trim();   // B
    return cleaned ? ' (' + cleaned + ')' : ' ';                                   // D: empty → drop
  });

  s = s
    // A (bare): "…; see product brochure for more details."
    .replace(/\s*[;,]?\s*see\b[^.;]*\bbrochure\b[^.;]*\.?/gi, '')
    // F
    .replace(/\s*Increased (?:benefit|maximum) starting in \d+(?:st|nd|rd|th) calendar year\.?/gi, '')
    // E: bare TRAILING qualifier (end / before . ;); mid-sentence prose is left alone
    .replace(/,?\s*per person,?\s*per calendar year(?=\s*[.;]|\s*$)/gi, '')
    // drop the generic "benefits paid in addition to other insurance, up to the selected max" disclaimer
    .replace(/\s*Benefits are paid in addition to other insurance coverage[^.]*\.?/gi, '')
    // remove the filler word "policy"
    .replace(/\bpolicy\b\s*/gi, '')
    // remove the "in most states" qualifier (keeps the condition it trails, e.g. the waiting period)
    .replace(/\s+in most states\b/gi, '')
    // cleanup
    .replace(/\(\s*\)/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;])/g, '$1')
    .trim();

  if (s) s = s.charAt(0).toUpperCase() + s.slice(1);  // capitalize leading word
  return s;
}

/** "YYYY-MM-DD" → "MM/DD/YYYY" for the human-typed date field; "" for empty / non-ISO input. */
export function isoToMDY(iso) {
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso == null ? '' : iso));
  return m ? (m[2] + '/' + m[3] + '/' + m[1]) : '';
}

/** "MM/DD/YYYY" (or "M/D/YYYY") → "YYYY-MM-DD"; "" if incomplete or not a real calendar date. */
export function mdyToISO(mdy) {
  var m = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})\s*$/.exec(String(mdy == null ? '' : mdy));
  if (!m) return '';
  var mm = +m[1], dd = +m[2], yyyy = +m[3];
  var d = new Date(yyyy, mm - 1, dd);
  // reject out-of-range + overflow (e.g. 02/30 → Mar 2) by requiring the parts to round-trip
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return '';
  return yyyy + '-' + String(mm).padStart(2, '0') + '-' + String(dd).padStart(2, '0');
}

/**
 * Live input mask for the MM/DD/YYYY date twins: reflow whatever the user typed into segments,
 * auto-appending the / as MM and DD complete. A separator typed early (7/, 07/4/ — also - and .)
 * zero-pads its segment, so pasted M/D/YYYY dates reflow correctly too. `deleting` suppresses the
 * trailing separator so backspacing over a / doesn't immediately re-append it (mask never sticks).
 */
export function maskDateInput(value, deleting) {
  var WIDTHS = [2, 2, 4];
  var segs = [''];
  var s = String(value == null ? '' : value);
  for (var i = 0; i < s.length && !(segs.length === 3 && segs[2].length === 4); i++) {
    var ch = s.charAt(i);
    var cur = segs.length - 1;
    if (ch >= '0' && ch <= '9') {
      segs[cur] += ch;
      if (segs[cur].length === WIDTHS[cur] && cur < 2) segs.push('');
    } else if ((ch === '/' || ch === '-' || ch === '.') && segs[cur] && cur < 2) {
      segs[cur] = segs[cur].padStart(WIDTHS[cur], '0');   // early separator: zero-pad the segment
      segs.push('');
    }
  }
  if (deleting && segs.length > 1 && segs[segs.length - 1] === '') segs.pop();
  return segs.join('/');
}

export const Format = { escapeHtml, formatCurrency, formatDateForInput, debounce, stripBenefitBoilerplate, isoToMDY, mdyToISO, maskDateInput };
